import { Pagination } from '@ovComponents/2-component/pagination/pagination';
import {
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import type { Row, Table as TanstackTable } from '@tanstack/react-table';
import type { Theme } from 'hephaestus/core';
import { Box, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { SortIndicator } from '../hooks/useTradingTable';
import {
  ALIGNED_TABLE_SX,
  BORDERED_BOX_SX,
  SECTION_HEADER_SX,
  SORTABLE_HEADER_SX,
} from '../styles';
import type { PortfolioReport } from '../types';
import type { EnrichedHolding } from '../utils/portfolioRevampBuilders';
import { PortfolioRowDetailPositionsRow } from './portfolioRowDetailPositionsRow';
export type PortfolioRowDetailPositionsProps = {
  report: PortfolioReport;
  holdingTable: TanstackTable<EnrichedHolding>;
  pagedPositionRows: Row<EnrichedHolding>[];
  positionsPage: number;
  setPositionsPage: (page: number) => void;
  positionsTotalPages: number;
  theme: Theme;
};

export const PortfolioRowDetailPositions = ({
  report,
  holdingTable,
  pagedPositionRows,
  positionsPage,
  setPositionsPage,
  positionsTotalPages,
  theme,
}: PortfolioRowDetailPositionsProps) => {
  const { t } = useTranslation('portfolioTradingHub');

  const holdingDisplayColumns: Array<{
    id: string;
    label: string;
    width: string;
    minWidth: string;
    number?: boolean;
  }> = [
    {
      id: 'symbol',
      label: t('rowDetail.positions.symbol'),
      width: '18%',
      minWidth: '120px',
    },
    {
      id: 'currency',
      label: t('rowDetail.positions.currency'),
      width: '7%',
      minWidth: '48px',
    },
    {
      id: 'quantity',
      label: t('rowDetail.positions.qty'),
      width: '11%',
      minWidth: '72px',
      number: true,
    },
    {
      id: 'price',
      label: t('rowDetail.positions.price'),
      width: '11%',
      minWidth: '72px',
      number: true,
    },
    {
      id: 'value',
      label: t('rowDetail.positions.value'),
      width: '12%',
      minWidth: '84px',
      number: true,
    },
    {
      id: 'curPct',
      label: t('rowDetail.positions.currentPct'),
      width: '9%',
      minWidth: '60px',
      number: true,
    },
    {
      id: 'tgtPct',
      label: t('rowDetail.positions.targetPct'),
      width: '9%',
      minWidth: '60px',
      number: true,
    },
    {
      id: 'driftPct',
      label: t('rowDetail.positions.drift'),
      width: '10%',
      minWidth: '64px',
      number: true,
    },
    {
      id: 'driftVal',
      label: t('rowDetail.positions.driftValue'),
      width: '13%',
      minWidth: '76px',
      number: true,
    },
  ];

  return (
    <Box
      sx={{
        mb: 2,
      }}
    >
      <Typography
        variant='labelSmall'
        component='div'
        sx={{ mb: 1, ...SECTION_HEADER_SX }}
      >
        {t('rowDetail.positions.title', { count: report.holdings.length })}
      </Typography>
      <Box sx={{ overflowX: 'auto', ...BORDERED_BOX_SX }}>
        <Table
          sx={{
            backgroundColor: theme.color.surface,
            ...ALIGNED_TABLE_SX,
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: theme.color.background }}>
              {holdingDisplayColumns.map((col) => {
                const column = holdingTable.getColumn(col.id);
                return (
                  <TableHeadCell
                    key={col.id}
                    number={col.number}
                    right={col.number}
                    onClick={column?.getToggleSortingHandler()}
                    sx={{
                      padding: '6px 16px',
                      width: col.width,
                      minWidth: col.minWidth,
                      ...SORTABLE_HEADER_SX,
                    }}
                  >
                    {col.label}
                    <SortIndicator sorted={column?.getIsSorted() || false} />
                  </TableHeadCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedPositionRows.map((row) => {
              const holding = row.original;
              return (
                <PortfolioRowDetailPositionsRow
                  key={holding.financialProduct?.id || row.id}
                  holding={holding}
                  theme={theme}
                />
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {positionsTotalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 1.5,
          }}
        >
          <Pagination
            count={positionsTotalPages}
            page={positionsPage}
            onChange={(_: unknown, newPage: number) =>
              setPositionsPage(newPage)
            }
          />
        </Box>
      )}
    </Box>
  );
};
